import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw910cf1Api, F90C } from '../../../services/pw910cf1Api';

export type Mode = 'browse' | 'add' | 'edit';

export function usePW910CF1Form() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90C[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [maritalStatus, setMaritalStatus] = useState(0);
  const [gender, setGender] = useState(0);
  const [school, setSchool] = useState('');
  const [department, setDepartment] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [regAddress, setRegAddress] = useState('');
  const [emergContact, setEmergContact] = useState('');
  const [emergPhone, setEmergPhone] = useState('');
  const [hometown, setHometown] = useState('');
  const [applyDate, setApplyDate] = useState('');
  const [applyDept, setApplyDept] = useState('');
  const [applyTitle, setApplyTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [salary, setSalary] = useState('');
  const [engName, setEngName] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pw910cf1Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f90c_01);
      setName(current.f90c_11);
      setIdNumber(current.f90c_12);
      setBirthDate(current.f90c_13 ? current.f90c_13.substring(0, 10) : '');
      setMaritalStatus(current.f90c_14);
      setGender(current.f90c_15);
      setSchool(current.f90c_16);
      setDepartment(current.f90c_17);
      setGradYear(current.f90c_18);
      setPhone(current.f90c_19);
      setMobile(current.f90c_20);
      setAddress(current.f90c_25);
      setRegAddress(current.f90c_26);
      setEmergContact(current.f90c_27);
      setEmergPhone(current.f90c_28);
      setHometown(current.f90c_29);
      setApplyDate(current.f90c_31 ? current.f90c_31.substring(0, 10) : '');
      setApplyDept(current.f90c_32);
      setApplyTitle(current.f90c_33);
      setStartDate(current.f90c_35 ? current.f90c_35.substring(0, 10) : '');
      setHeight(current.f90c_42);
      setWeight(current.f90c_43);
      setSalary(current.f90c_53);
      setEngName(current.f90c_55);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Clear form for add
  const clearForm = () => {
    setCode(''); setName(''); setIdNumber(''); setBirthDate('');
    setMaritalStatus(0); setGender(0); setSchool(''); setDepartment('');
    setGradYear(''); setPhone(''); setMobile(''); setAddress('');
    setRegAddress(''); setEmergContact(''); setEmergPhone('');
    setHometown(''); setApplyDate(''); setApplyDept(''); setApplyTitle('');
    setStartDate(''); setHeight(''); setWeight(''); setSalary(''); setEngName('');
  };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    clearForm();
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f90c_01);
    setName(current.f90c_11);
    setIdNumber(current.f90c_12);
    setBirthDate(current.f90c_13 ? current.f90c_13.substring(0, 10) : '');
    setMaritalStatus(current.f90c_14);
    setGender(current.f90c_15);
    setSchool(current.f90c_16);
    setDepartment(current.f90c_17);
    setGradYear(current.f90c_18);
    setPhone(current.f90c_19);
    setMobile(current.f90c_20);
    setAddress(current.f90c_25);
    setRegAddress(current.f90c_26);
    setEmergContact(current.f90c_27);
    setEmergPhone(current.f90c_28);
    setHometown(current.f90c_29);
    setApplyDate(current.f90c_31 ? current.f90c_31.substring(0, 10) : '');
    setApplyDept(current.f90c_32);
    setApplyTitle(current.f90c_33);
    setStartDate(current.f90c_35 ? current.f90c_35.substring(0, 10) : '');
    setHeight(current.f90c_42);
    setWeight(current.f90c_43);
    setSalary(current.f90c_53);
    setEngName(current.f90c_55);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f90c_01);
      setName(current.f90c_11);
    }
  };

  // Build request payload
  const buildPayload = () => ({
    f90c_11: name,
    f90c_12: idNumber,
    f90c_13: birthDate || null,
    f90c_14: maritalStatus,
    f90c_15: gender,
    f90c_16: school,
    f90c_17: department,
    f90c_18: gradYear,
    f90c_19: phone,
    f90c_20: mobile,
    f90c_25: address,
    f90c_26: regAddress,
    f90c_27: emergContact,
    f90c_28: emergPhone,
    f90c_29: hometown,
    f90c_31: applyDate || null,
    f90c_32: applyDept,
    f90c_33: applyTitle,
    f90c_35: startDate || null,
    f90c_42: height,
    f90c_43: weight,
    f90c_53: salary,
    f90c_55: engName,
  });

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await pw910cf1Api.create({ f90c_01: code, ...buildPayload() });
        message.success('新增成功');
      } else {
        await pw910cf1Api.update(current!.f90c_01, buildPayload());
        message.success('修改成功');
      }
      const targetCode = mode === 'add' ? code : current!.f90c_01;
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f90c_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await pw910cf1Api.delete(current.f90c_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f90c_01.includes(searchText) || r.f90c_11.includes(searchText));
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, name, setName,
    idNumber, setIdNumber, birthDate, setBirthDate,
    maritalStatus, setMaritalStatus, gender, setGender,
    school, setSchool, department, setDepartment,
    gradYear, setGradYear, phone, setPhone,
    mobile, setMobile, address, setAddress,
    regAddress, setRegAddress, emergContact, setEmergContact,
    emergPhone, setEmergPhone, hometown, setHometown,
    applyDate, setApplyDate, applyDept, setApplyDept,
    applyTitle, setApplyTitle, startDate, setStartDate,
    height, setHeight, weight, setWeight,
    salary, setSalary, engName, setEngName,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  };
}
